import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchSiteConfig, fetchI18n, reportStrings } from "@/lib/apiData";

const SiteContext = createContext(null);
export const LANG_LABELS = { en: "English", nl: "Nederlands", de: "Deutsch" };

const pending = new Set();
const reported = new Set();
let timer = null;
const queueReport = (text) => {
  if (reported.has(text)) return;
  reported.add(text);
  pending.add(text);
  clearTimeout(timer);
  timer = setTimeout(() => {
    const batch = [...pending].slice(0, 80);
    batch.forEach((s) => pending.delete(s));
    reportStrings(batch).catch(() => {});
    if (pending.size) queueReport([...pending][0]);
  }, 1500);
};

const pickLang = (langs) => {
  const enabled = langs?.enabled || ["en"];
  const saved = localStorage.getItem("jl_lang");
  if (saved && enabled.includes(saved)) return saved;
  if (langs?.auto_detect) {
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
    if (enabled.includes(nav)) return nav;
  }
  return langs?.default || "en";
};

export function SiteProvider({ children }) {
  const { data: config } = useQuery({ queryKey: ["site-config"], queryFn: fetchSiteConfig, staleTime: 30_000 });
  const [lang, setLangState] = useState(null);

  useEffect(() => {
    if (config && !lang) setLangState(pickLang(config.languages));
  }, [config, lang]);

  const { data: dict = {}, isFetched } = useQuery({ queryKey: ["i18n", lang || "en"], queryFn: () => fetchI18n(lang || "en"), staleTime: 30_000, refetchInterval: 60_000 });

  const setLang = useCallback((l) => { localStorage.setItem("jl_lang", l); setLangState(l); }, []);

  const t = useCallback((text) => {
    if (typeof text !== "string" || !text.trim()) return text;
    const key = text.trim();
    if (isFetched && !(key in dict)) queueReport(key);
    return lang && lang !== "en" && dict[key] ? dict[key] : text;
  }, [dict, lang, isFetched]);

  useEffect(() => { document.documentElement.lang = lang || "en"; }, [lang]);

  const value = useMemo(() => ({ config, lang: lang || "en", setLang, t }), [config, lang, setLang, t]);
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error("useSite must be used within SiteProvider");
  return ctx;
}
